import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { updateNavbarHeading } from "../../../../constants/layout/store/filterSlice";
import { API_URL } from "../../../../constants";
import { GetServiceCaller } from "../../../../services/serviceCaller";
import { Table, Form } from "react-bootstrap";
import Pagination from "../../../../components/Pagination";
import SearchBar from "../../../../components/SearchBar";
import ExportToExcel from "../../../../components/ExportToExcel";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaSpinner } from "react-icons/fa";
import Select from "react-select";

const LeaveLedger = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(updateNavbarHeading("Leave Ledger"));
  }, []);

  const [searchTerm, setSearchTerm] = useState("");
  const [ledger, setLedger] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [monthList, setMonthList] = useState([]);
  const [apiStatus, setApiStatus] = useState(undefined);
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGetDataLoading, setIsGetDataLoading] = useState(false);

  // Column visibility configuration
  const [columnVisibility, setColumnVisibility] = useState({
    Code: true,
    Name: true,
    Leave: true,
    OpeningBalance: true,
    LeaveApplied: true,
    LeaveTaken: true,
    LeaveAccrued: true,
    ClosingBalance: true,
  });

  const columns = [
    { id: "Code", label: "EMP ID", accessor: "Code" },
    { id: "Name", label: "Employee", accessor: "Name" },
    { id: "Leave", label: "Leave", accessor: "Leave" },
    {
      id: "OpeningBalance",
      label: "Opening Balance",
      accessor: "OpeningBalance",
    },
    { id: "LeaveApplied", label: "Leave Applied", accessor: "LeaveApplied" },
    { id: "LeaveTaken", label: "Leave Taken", accessor: "LeaveTaken" },
    { id: "LeaveAccrued", label: "Leave Accrued", accessor: "LeaveAccrued" },
    {
      id: "ClosingBalance",
      label: "Closing Balance",
      accessor: "ClosingBalance",
    },
  ];

  const fetchReportList = async () => {
    try {
      setIsLoading(true);
    
      const response = await GetServiceCaller(
        `${API_URL}leave-ledger?P_EmployeeID=&P_MonthID=`
      );

      const ledger = response?.data?.leaveledger;
      setLedger(ledger);

      // Employee list
      const employeeList = response.data.empList.map((tech) => ({
        value: tech.value,
        label: tech.Name,
      }));
      setEmployeeList(employeeList);

      // Month list
      const monthlist = response.data.month.map((tech) => ({
        value: tech.value,
        label: tech.text,
      }));
      setMonthList(monthlist);
      setApiStatus(response?.status);
    } catch (error) {
      console.error("Error fetching reports", error);
    } finally {
      setIsLoading(false); // Stop loading regardless of success/error
    }
  };

  useEffect(() => {
    fetchReportList();
  }, []);

  // Sorting function
  const sortedData = useMemo(() => {
    let sortableData = [...ledger];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        // Handle numeric fields differently
        const numericFields = [
          "OpeningBalance",
          "LeaveAccrued",
          "ClosingBalance",
          "LeaveApplied",
          "LeaveTaken",
        ];
        const isNumeric = numericFields.includes(sortConfig.key);

        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (isNumeric) {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else {
          aValue = aValue?.toString().toLowerCase() || "";
          bValue = bValue?.toString().toLowerCase() || "";
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [ledger, sortConfig]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return sortedData;

    return sortedData.filter((item) => {
      return Object.keys(item).some((key) => {
        // Skip filtering for numeric values that we don't want to search on
        if (
          [
            "OpeningBalance",
            "LeaveApplied",
            "LeaveTaken",
            "LeaveAccrued",
            "ClosingBalance",
          ].includes(key)
        ) {
          return false;
        }
        return String(item[key])
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });
    });
  }, [sortedData, searchTerm]);

  // Pagination calculations
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Get current items for pagination
  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage, totalItems]);

  const recordsPerPageOptions = [
    { value: 10, label: "10" },
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
    { value: filteredData.length, label: "All" },
  ];

  const toggleColumnVisibility = (columnId) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Sort handler
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Get sort icon for header
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i class="bi bi-chevron-down"></i>;
    return sortConfig.direction === "asc" ? (
     <i class="bi bi-chevron-up"></i>
    ) : (
   <i class="bi bi-chevron-down"></i>
    );
  };
  const handleGetData = async() => {
    try {
      setIsGetDataLoading(true);
      setIsLoading(true);
      const employeeParam =
        (selectedEmployee ? selectedEmployee.value : "");
      const monthParam = selectedMonth ? selectedMonth.value : "";
      const response = await GetServiceCaller(
        `${API_URL}leave-ledger?P_EmployeeID=${employeeParam}&P_MonthID=${monthParam}`
      );

      const ledger = response?.data?.leaveledger;
      setLedger(ledger);

      // Employee list
      if (response?.data?.empList) {
        const employeeList = response.data.empList.map((tech) => ({
          value: tech.value,
          label: tech.Name,
        }));
        setEmployeeList(employeeList);
      }

      // Month list
      if (response?.data?.month) {
        const monthlist = response.data.month.map((tech) => ({
          value: tech.value,
          label: tech.text,
        }));
        setMonthList(monthlist);
      }
      setApiStatus(response?.status);
    } catch (error) {
      console.error("Error fetching reports", error);
    } finally {
      setIsGetDataLoading(false);
      setIsLoading(false); // Stop loading regardless of success/error
      setShowDropdown(false); // Close the dropdown after fetching data
    }
  };

  // Handle reset button click
  const handleReset = () => {
    setSelectedEmployee(null);
    setSelectedMonth(null);
    fetchReportList( ); // Reset to default values
    setShowDropdown(false); // Close the dropdown
  };

  return (
    <div className="container-fluid" id="skill_section">
      <div className="container-fluid" id="skill-table-area">
     
          <div className="d-flex justify-content-between">
            <div className="d-flex">
              <div className="me-1">
                <SearchBar
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  placeholder="Search by Code, Name or Leave..."
                />
              </div>
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}
              >
                <div className="btn-group me-2">
                  <button
                    type="button"
                    className="dropdown-toggle skill-button"
                    data-bs-toggle="dropdown"
                    onClick={() => setShowDropdown(!showDropdown)}
                    aria-expanded="false"
                    id="excel-btn"
                  >
                    <i className="bi bi-funnel-fill"></i>
                  </button>
                  <ul
                    className={`dropdown-menu p-2 project-status-filter ${
                      showDropdown ? "show" : ""
                    }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="row">
                      <div className="col-md-4">
                        <div class="mb-3">
                          <label for="Code" class="form-label">
                            Employee
                          </label>
                          <Select
                            id="employee-select"
                            options={employeeList}
                            value={selectedEmployee}
                            onChange={setSelectedEmployee}
                            placeholder="Select Employee"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div class="mb-3">
                          <label for="Code" class="form-label">
                            Month
                          </label>
                          <Select
                            id="month-select"
                            options={monthList}
                            value={selectedMonth}
                            onChange={setSelectedMonth}
                            placeholder="Select Month"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="text-end">
                        <button
                          className="custom-btn mx-2"
                          onClick={handleReset}
                          disabled={isGetDataLoading}
                        >
                          Reset
                        </button>
                        <button
                          className="custom-btn d-inline-flex align-items-center"
                          onClick={handleGetData}
                          disabled={isGetDataLoading}
                        >
                          {isGetDataLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Loading...
                            </>
                          ) : (
                            "Get Data"
                          )}
                        </button>
                      </div>
                    </div>
                  </ul>
                </div>
              </OverlayTrigger>
            </div>
            <div className="d-flex justify-content-end">
              <div className="me-1">
                <ExportToExcel
                  data={filteredData}
                  filename="LeaveLedger.csv"
                  columns={columns.filter((col) => columnVisibility[col.id])}
                />
              </div>
              <div className="d-flex justify-content-end">
                <div className="me-1">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-top">Column Chooser</Tooltip>}
                  >
                    <div className="btn-group me-2">
                      <button
                        type="button"
                        className="dropdown-toggle skill-button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        id="excel-btn"
                      >
                        <i className="bi bi-list"></i>
                      </button>
                      <ul className="dropdown-menu p-2">
                        <div className="row">
                          <Form.Group>
                            {columns.map((column) => (
                              <Form.Check
                                key={column.id}
                                type="checkbox"
                                id={`checkbox-${column.id}`}
                                label={column.label}
                                checked={columnVisibility[column.id]}
                                onChange={() =>
                                  toggleColumnVisibility(column.id)
                                }
                              />
                            ))}
                          </Form.Group>
                        </div>
                      </ul>
                    </div>
                  </OverlayTrigger>
                </div>
              </div>
            </div>
          </div>
   <div className="table-responsive">
          <Table bordered striped hover>
            <thead id="thcolor" className="text-center">
              <tr>
                {columns.map(
                  (column) =>
                    columnVisibility[column.id] && (
                      <th
                        key={column.id}
                        onClick={() => requestSort(column.accessor)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-center">
                          {column.label}
                          {getSortIcon(column.accessor)}
                        </div>
                      </th>
                    )
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-4">
                    <div className="d-flex justify-content-center">
                      <FaSpinner class="spinner" />
                    </div>
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                <>
                  {currentItems.map((item, index) => (
                    <tr key={index}>
                      {columns.map(
                        (column) =>
                          columnVisibility[column.id] && (
                            <td
                              key={column.id}
                              className={
                                [
                                  "OpeningBalance",
                                  "LeaveAccrued",
                                  "LeaveTaken",
                                  "LeaveApplied",
                                  "ClosingBalance",
                                ].includes(column.id)
                                  ? "text-end"
                                  : column.id === "Name" ||column.id === "Code"
                                  ? "text-start"
                                  : "text-center"
                              }
                            >
                              {[
                                "OpeningBalance",
                                "LeaveAccrued",
                                "ClosingBalance",
                              ].includes(column.id)
                                ? parseFloat(item[column.accessor]).toFixed(2)
                                : item[column.accessor]}
                            </td>
                          )
                      )}
                    </tr>
                  ))}
                  {/* Summary row */}
                  <tr className="fw-bold">
                    {columns.map((column) => {
                      if (!columnVisibility[column.id]) return null;

                      if (column.id === "Name") {
                        return (
                          <td key="count" className="text-start">
                            Count: {filteredData.length}
                          </td>
                        );
                      } else if (column.id === "ClosingBalance") {
                        const totalClosingBalance = filteredData.reduce(
                          (sum, item) => {
                            return sum + parseFloat(item.ClosingBalance || 0.00);
                          },
                          0.00
                        );
                        return (
                          <td key="total-closing" className="text-end">
                            {totalClosingBalance.toFixed(2)}
                          </td>
                        );
                      } else {
                        return <td key={column.id}></td>;
                      }
                    })}
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
 </div>
            <div className="d-flex justify-content-between mb-3">
              <div>
                <span className="d-flex align-items-center">
                  Showing {(currentPage - 1) * itemsPerPage + 1} -{" "}
                  {Math.min(currentPage * itemsPerPage, filteredData.length)} of{" "}
                  {filteredData.length}
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="mx-2 form-select-sm"
                  >
                    {recordsPerPageOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </span>
              </div>
              <div>
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>

        </div>
     
    </div>
  );
};

export default LeaveLedger;
